/**
 * SageTriggerButton.tsx
 *
 * Floating trigger button for the Sage co-pilot panel.
 * Shows a MessageSquare icon (not the avatar) in sage green.
 * Pulses when there are pending suggested questions and the panel is closed.
 *
 * Effects: magnetic hover (scale + shadow lift), glow halo, pulse ring.
 */
import { MessageSquare } from "lucide-react";
import { SAGE } from "./config";
import { fx } from "@/lib/effects";
import { cn } from "@/lib/utils";

interface SageTriggerButtonProps {
  onClick: () => void;
  hasPendingSuggestion: boolean;
  isOpen: boolean;
}

export default function SageTriggerButton({
  onClick,
  hasPendingSuggestion,
  isOpen,
}: SageTriggerButtonProps) {
  // Hide the trigger when the panel is open (desktop: panel is visible; mobile: drawer is open)
  if (isOpen) return null;

  const shouldPulse = hasPendingSuggestion && !isOpen;

  return (
    <button
      onClick={onClick}
      aria-label="Open Sage co-pilot"
      data-tour="sage-trigger"
      className={cn(
        // sage-trigger exempts this button from the global button { position: relative } rule
        // in index.css — without it, the fixed positioning is overridden and the button
        // appears bottom-left instead of bottom-right.
        "sage-trigger",
        "fixed bottom-[calc(100px+env(safe-area-inset-bottom,0px))] md:bottom-8 right-4 md:right-8 z-[9999] w-16 h-16 rounded-full",
        "flex items-center justify-center",
        "shadow-[0_4px_16px_-2px_rgba(71,130,95,0.35),0_2px_8px_-2px_rgba(71,130,95,0.2)]",
        fx.float,
      )}
      style={{ background: SAGE }}
    >
      <MessageSquare className="w-6 h-6 text-white transition-transform duration-200 group-hover:scale-110" />

      {/* Ambient glow ring — always visible, subtle */}
      <span
        className="absolute inset-[-3px] rounded-full pointer-events-none"
        style={{
          background: `radial-gradient(circle, ${SAGE}20 60%, transparent 100%)`,
          animation: "magneticGlow 3s ease-in-out infinite",
        }}
      />

      {/* Pulse ring — only when pending suggestion */}
      {shouldPulse && (
        <span
          className="absolute inset-0 rounded-full pointer-events-none"
          style={{
            border: `2px solid ${SAGE}`,
            animation: "sagePulseRing 2s ease-out infinite",
          }}
        />
      )}
    </button>
  );
}
